import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { v3 } from '@feedflo/tlv';
import { DATE_FORMAT_DASH, DATE_TIME_FORMAT_SHORT, DATE_TIME_FORMAT_FULL } from '../../utils/dates';
import FeedFloPercent from '../../atoms/FeedFloPercent/FeedFloPercent';
import DeviceCardView from './DeviceCardView';
import TransactionChart from '../TransactionChart';
import StatusBadge from '../../atoms/StatusBadge/StatusBadge';
import SensorChart from './SensorDataChart';
import { censorTypes, useCensor } from '../../utils/hooks/useCensor';
import CopyButton from '../../atoms/CopyButton';
import DeviceAlgorithmOverride from './DeviceAlgorithmOverride';
import { DEVICE_GQL } from './queries';

const SHORT_WINDOW = 60 * 60;
const LONG_WINDOW_DAYS = 5;
const LONG_WINDOW = LONG_WINDOW_DAYS * 24 * 60 * 60;

const shortenAlgorithmVersion = (version) => {
  if (!version) return version;
  return version.length > 8 ? `${version.substring(0, 8)}...` : version;
};

export default function DeviceCard({ deviceId, serial = '' }) {
  const now = useMemo(() => dayjs.tz(), []);
  const { censor } = useCensor();

  let runTimeError = null;
  const {
    loading,
    error: gqlError,
    data,
  } = useQuery(DEVICE_GQL, {
    variables: {
      deviceId,
      now: now.subtract(5, 'minutes').unix(),
      shortTimeWindow: now.subtract(5, 'minutes').subtract(SHORT_WINDOW, 'seconds').unix(),
      longTimeWindow: now.subtract(5, 'minutes').subtract(LONG_WINDOW, 'seconds').unix(),
    },
  });

  const device = data?.device[0];
  const isBinController = device?.type === 'BinController';
  const deviceType = device?.type || (isBinController ? 'BinController' : 'FeedLine');
  const lastUploadTimestamp =
    device?.lastTransaction?.[0]?.occured_at > 946684800 // after Y2K? Legit time. Before? Probably system time
      ? device?.lastTransaction?.[0]?.occured_at
      : device?.lastTransaction?.[0]?.created_at || null;
  const firstUploadTimestamp = device?.firstTransaction?.[0]?.occured_at || null;
  const installedTimestamp = device?.device_assignments?.[0]?.started_at || null;
  const orientation = {};

  const mostRecentDataEndedAt = device?.recent_data?.[0]?.ended_at || null;
  const mostRecentDataCreatedAt = device?.recent_data?.[0]?.created_at || null;
  const mostRecentResetTimestamp = device?.lastReset?.[0]?.started_at || null;

  // check for orientation signal block in most recent signal data
  for (let i = 0; i < device?.recent_data?.length; i++) {
    const hex = device.recent_data[i].data.substring(2);
    const arrayBuffer = new ArrayBuffer(hex.length / 2);
    const dataView = new DataView(arrayBuffer);
    for (let c = 0; c < hex.length; c += 2) {
      const byte = parseInt(hex.substring(c, c + 2), 16);
      dataView.setUint8(c / 2, byte);
    }
    const json = v3.TLVtoJSON(dataView);
    if (json.orientation) {
      Object.assign(orientation, json.orientation);
      break;
    }
  }

  let fwTag = null;
  try {
    if (device?.fwFaults[0]?.details) {
      const details = atob(device?.fwFaults[0]?.details);
      fwTag = details;
      const jDetails = JSON.parse(details);
      if (jDetails.fw) {
        fwTag = jDetails.fw;
      }
    }
  } catch (e) {
    console.error(e);
  }

  const quickFacts = useMemo(
    () => [
      {
        title: 'Last Upload',
        value: `${lastUploadTimestamp !== null ? dayjs.tz(1000 * lastUploadTimestamp).fromNow() : 'Unknown'}`,
        detail: `${lastUploadTimestamp !== null ? dayjs.tz(1000 * lastUploadTimestamp).format(DATE_TIME_FORMAT_FULL) : 'Unknown'
          }`,
      },
      {
        title: 'First Upload',
        value: `${firstUploadTimestamp !== null ? dayjs.tz(1000 * firstUploadTimestamp).format(DATE_FORMAT_DASH) : 'Unknown'
          }`,
        detail: `${firstUploadTimestamp !== null
          ? dayjs.tz(1000 * firstUploadTimestamp).format(DATE_TIME_FORMAT_SHORT)
          : 'Unknown'
          }`,
      },
      {
        title: 'Newest Data',
        value: `${mostRecentDataEndedAt !== null ? dayjs.tz(1000 * mostRecentDataEndedAt).fromNow() : 'Unknown'}`,
        detail: `Captured At: ${mostRecentDataEndedAt !== null
          ? dayjs.tz(1000 * mostRecentDataEndedAt).format(DATE_TIME_FORMAT_SHORT)
          : 'Unknown'
          }\nUploaded At: ${mostRecentDataCreatedAt !== null
            ? dayjs.tz(1000 * mostRecentDataCreatedAt).format(DATE_TIME_FORMAT_SHORT)
            : 'Unknown'
          }`,
      },
      {
        title: 'Newest Reset',
        value: `${mostRecentResetTimestamp !== null ? dayjs.tz(1000 * mostRecentResetTimestamp).fromNow() : 'Unknown'}`,
        detail: `${mostRecentResetTimestamp !== null
          ? dayjs.tz(1000 * mostRecentResetTimestamp).format(DATE_TIME_FORMAT_SHORT)
          : 'Unknown'
          }`,
      },
      {
        title: `${LONG_WINDOW_DAYS} Day Uptime`,
        value: (
          <FeedFloPercent
            min={98}
            max={102}
            toFixed={3}
            number={
              ((device?.long_data_window_aggregate?.aggregate?.sum?.ended_at -
                device?.long_data_window_aggregate?.aggregate?.sum?.started_at) /
                LONG_WINDOW) *
              100
            }
          />
        ),
      },
      {
        title: '1 Hour Uptime',
        value: (
          <FeedFloPercent
            min={98}
            max={102}
            toFixed={3}
            number={
              ((device?.short_data_window_aggregate?.aggregate?.sum?.ended_at -
                device?.short_data_window_aggregate?.aggregate?.sum?.started_at) /
                SHORT_WINDOW) *
              100
            }
          />
        ),
      },
      {
        title: 'Last Run',
        value: `${device?.lastCompletedAuger?.[0]?.started_at > 0
          ? dayjs.tz(1000 * device?.lastCompletedAuger[0]?.started_at).format(DATE_TIME_FORMAT_SHORT)
          : 'Unknown'
          }`,
      },
      { title: 'Auger Running', value: `${device?.augerRunning?.[0]?.ended_at === null}` || 'Unknown' },
      {
        title: 'FW Target',
        value: `${device?.firmware_branch?.name} [${device?.firmware_branch?.firmwares?.[0]?.tag}]`,
      },
      {
        title: 'FW Reported',
        value: `${fwTag}`,
        detail: 'The current firmware version as reported by the most recent SYSTEM_RESET event.',
      },
      { title: 'HW Version', value: device?.hw_version || 'unknown' },
      { title: 'PN', value: device?.part_number || 'unknown' },
      { title: 'MAC', value: device?.mac_address || 'unknown' },
      {
        title: 'Pitch',
        value: Number(orientation?.pitch).toFixed(1) + '°' || 'unknown',
        detail: 'The degree of vertical device tilt. Upwards nose tilt is positive.',
      },
      {
        title: 'Roll',
        value: Number(orientation?.roll).toFixed(1) + '°' || 'unknown',
        detail: 'The degree of horizontal device tilt. Left tilt is positive.',
      },
      {
        title: 'Movement',
        value: Number(orientation?.movementNoise).toFixed(1) || 'unknown',
        detail: `A high Movement score indicates that roll and pitch values are likely inaccurate. An ideal score is less than 1.0.`,
      },
      {
        title: 'Installed',
        value: lastUploadTimestamp !== null ? dayjs.tz(1000 * installedTimestamp).format(DATE_FORMAT_DASH) : 'Unknown',
        detail: 'The approximate time this device was installed.',
      },
      {
        title: 'Algorithm Version',
        value: device?.algorithm_version
          ? `${shortenAlgorithmVersion(device.algorithm_version)}${device?.device_algorithm_overrides?.[0] ||
            device?.device_assignments?.[0]?.feed_line?.farm?.barn_algorithm_overrides?.[0]
            ? ' (override)'
            : ' (default)'
          }`
          : 'Unknown',
        detail: device?.algorithm_version
          ? `Effective Algorithm Version: ${device.algorithm_version}\nDevice Override: ${device?.device_algorithm_overrides?.[0]
            ? `${device.device_algorithm_overrides[0].algorithm_version}${device.device_algorithm_overrides[0].algorithm?.description
              ? ` - ${device.device_algorithm_overrides[0].algorithm.description}`
              : ''
            }${device.device_algorithm_overrides[0].algorithm?.published_at ? ' (Published)' : ' (Unpublished)'}`
            : 'None'
          }\nBarn Override: ${device?.device_assignments?.[0]?.feed_line?.farm?.barn_algorithm_overrides?.[0]
            ? `${device.device_assignments[0].feed_line.farm.barn_algorithm_overrides[0].algorithm_version}${device.device_assignments[0].feed_line.farm.barn_algorithm_overrides[0].algorithm?.description
              ? ` - ${device.device_assignments[0].feed_line.farm.barn_algorithm_overrides[0].algorithm.description}`
              : ''
            }${device.device_assignments[0].feed_line.farm.barn_algorithm_overrides[0].algorithm?.published_at
              ? ' (Published)'
              : ' (Unpublished)'
            }`
            : 'None'
          }`
          : 'None'
      }
    ],
    [device],
  );
  const binController = device?.bin_controller;
  const binNames = binController?.bin_controller_bins?.map((bcb) => bcb.bin?.name).filter(Boolean).join(', ');

  const viewProps = {
    barnId: isBinController
      ? binController?.bin_set?.farm?.id
      : device?.device_assignments?.[0]?.feed_line?.farm?.id,
    barnTitle: isBinController
      ? censor(binController?.bin_set?.farm?.name, censorTypes.barn) || 'Unknown'
      : censor(device?.device_assignments?.[0]?.feed_line?.farm?.name, censorTypes.barn) || 'Unknown',
    feedLineTitle: isBinController
      ? binNames || 'Unknown'
      : censor(device?.device_assignments?.[0]?.feed_line?.name, censorTypes.feedline) || 'Unknown',
    quickFacts,
    algorithmOverrideSection: (
      <DeviceAlgorithmOverride
        deviceId={deviceId}
        deviceAlgorithmOverride={device?.device_algorithm_overrides[0]}
      />
    ),
  };
  const calibrationAnalyses = [];
  device?.factory_calibrations?.forEach((calibration) => {
    calibration?.factory_calibration_analyses?.forEach((analysis) => {
      const jsonBodyStr = analysis.json_body;

      calibrationAnalyses.push({
        date: calibration.started_at,
        started_at: calibration.started_at,
        ended_at: calibration.ended_at,
        status: analysis.status,
        version: analysis.version,
        json_body: jsonBodyStr,
      });
    });
  });

  return (
    <div>
      <DeviceCardView
        deviceId={deviceId}
        serial={serial}
        deviceType={deviceType}
        loading={loading}
        error={gqlError || runTimeError}
        feedLineTitle={viewProps.feedLineTitle}
        quickFacts={viewProps.quickFacts}
        algorithmOverrideSection={viewProps.algorithmOverrideSection}
        qaReports={device?.qa_reports?.map((report) => {
          let reportObject = {};

          // The QA system was accidentally changed to store QA reports as JSON-encoded strings instead of JSON,
          // so we need handle both cases here
          if (typeof report?.body === 'string') {
            reportObject = JSON.parse(report?.body);
          } else if (typeof report?.body === 'object') {
            reportObject = report?.body;
          }

          return {
            id: report.id,
            date: report.created_at,
            status: report.status,
            type: report.type,
            errorDetail: reportObject?.errorDetail,
            tests: reportObject?.Report,
          };
        })}
        calibrations={deviceType === 'FeedLine' ? calibrationAnalyses : undefined}
        inductiveSensorTestViews={
          deviceType === 'FeedLine'
            ? device?.device_inductive_sensor_tests.map((calibration, i) => (
              <div key={i} className="DeviceCard-lensCalibrationSection">
                {calibration?.inductive_sensor_test_analyses.map((analysis) => {
                  let jsonDataToCopy = 'Error';
                  try {
                    jsonDataToCopy = `${JSON.stringify(JSON.parse(analysis?.json_body), null, 2)}`;
                  } catch (error) {
                    console.error(error);
                  }

                  return (
                    <>
                      <StatusBadge
                        key={analysis.id}
                        status={analysis.status === 'pass' ? 'success' : 'error'}
                        text={analysis.status || 'blank'}
                      />{' '}
                      <CopyButton data={jsonDataToCopy} />{' '}
                    </>
                  );
                })}
                <br />
                <Link to={`/ rawData / ${deviceId} ? from = ${calibration.started_at} & to=${calibration.ended_at}`}>
                  📈 Full Screen
                </Link>
                <SensorChart
                  deviceId={deviceId}
                  from={dayjs.tz(calibration.started_at * 1000)}
                  to={dayjs.tz(calibration.ended_at * 1000)}
                />
              </div>
            ))
            : undefined
        }
        faultCodes={device?.faultCodes?.map((fcode) => {
          return {
            code: fcode.code,
            started_at: fcode.started_at * 1000, // converting to milliseconds
            ended_at: fcode.ended_at * 1000, // converting to milliseconds
            id: fcode.id,
          };
        })}
        deviceData={device?.device_data?.map((data) => {
          // Calculate size in bytes from hex string (each byte is 2 hex chars, minus "0x" prefix)
          const sizeInBytes = data.data ? (data.data.length - 2) / 2 : 0;

          return {
            id: data.id,
            id_from_device: data.id_from_device,
            started_at: data.started_at * 1000, // converting to milliseconds
            ended_at: data.ended_at * 1000, // converting to milliseconds
            created_at: data.created_at * 1000, // converting to milliseconds
            sizeInBytes,
          };
        })}
        lensCalibrations={
          deviceType === 'FeedLine'
            ? device?.lens_calibrations.map((calibration) => (
              <div key={calibration.id} className="DeviceCard-lensCalibrationSection">
                <Link to={`/ rawData / ${deviceId} ? from = ${calibration.started_at} & to=${calibration.ended_at}`}>
                  Full Screen
                </Link>
                <SensorChart
                  deviceId={deviceId}
                  from={dayjs.tz(calibration.started_at * 1000)}
                  to={dayjs.tz(calibration.ended_at * 1000)}
                />
              </div>
            ))
            : undefined
        }
        transactionChart={
          <>
            <div className="transactionHolder">
              {device?.sampleTransaction?.map((transaction) => {
                const icon = transaction.status === '200' ? '👍' : '❌';
                const shortDate = dayjs.tz(1000 * transaction?.occured_at || 0).fromNow();
                const longDate = dayjs.tz(1000 * transaction?.occured_at || 0).format(DATE_TIME_FORMAT_FULL);
                return (
                  <div className="transactions" key={transaction.id}>
                    <div title={longDate}>{shortDate}</div>
                    <div title={transaction.status}>
                      {icon}
                      {transaction.sub_type.toUpperCase()}: {transaction.data}
                    </div>
                  </div>
                );
              })}
            </div>
            <TransactionChart deviceId={deviceId} from={now.subtract(2, 'week')} to={now} />
          </>
        }
        rawDataChart={<SensorChart deviceId={deviceId} from={now.subtract(6, 'hour')} to={now} />}
        {...viewProps}
      />
    </div>
  );
}

DeviceCard.propTypes = {
  deviceId: PropTypes.string,
  serial: PropTypes.string,
};
